// media-access.service.ts

import { Injectable } from '@angular/core';
import { interval, Subscription, BehaviorSubject } from 'rxjs';
import { ExaminationService } from 'src/app/examination/examination.service';
import { MobileFeedDto } from '../model/mobile-feed-dto';
@Injectable({
  providedIn: 'root',
})
export class MediaAccessService {
  private permissionCheckSubscription: Subscription;
  private mediaPermissionsSubject: BehaviorSubject<any> =
    new BehaviorSubject<any>({});
  private audioContext?: AudioContext;
  public mediaPermissions$ = this.mediaPermissionsSubject.asObservable();
  activeTab: number;
  constructor(private readonly _examinationService: ExaminationService) {}

  private async checkMediaAccess(): Promise<void> {
    let audioEnabled: boolean;
    let videoEnabled: boolean;
    let extension: boolean;
    if (this.activeTab >= 1) {
      // audioEnabled = await this.isAudioEnabled();//microphone
      // videoEnabled = await this.isVideoEnabled();//video
    } else {
      extension = this.checkExtensionPresence();
    }
    this.mediaPermissionsSubject.next([
      {
        audio: audioEnabled,
        video: videoEnabled,
        extension: extension /*, screenEnabled*/,
      },
    ]);
  }

  public startContinuousPermissionCheck(): void {
    const intervalTime = 1000; // 5 seconds (adjust as needed)

    this.permissionCheckSubscription = interval(intervalTime).subscribe(() =>
      this.checkMediaAccess(),
    );
  }

  public stopContinuousPermissionCheck(): void {
    if (this.permissionCheckSubscription) {
      this.permissionCheckSubscription.unsubscribe();
    }
  }

  async isAudioEnabled(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error) {
      return false;
    }
  }

  async isVideoEnabled(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error) {
      return false;
    }
  }

  async isScreenEnabled(): Promise<boolean> {
    try {
      const stream = await (navigator.mediaDevices as any).getDisplayMedia({
        video: true,
      });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error) {
      return false;
    }
  }
  isFullScreenEnabled(): boolean {
    let isFullScreen: boolean;
    if (document.fullscreenElement) {
      isFullScreen = true;
    } else {
      isFullScreen = false;
    }
    return isFullScreen;
  }
  isSurroundingVerified() {
    return new Promise<boolean>((resolve, reject) => {
      this._examinationService.getSurroundingVerified().subscribe({
        next: (response) => {
          resolve(response.data);
        },
        error: (error) => {
          reject(error.error.data);
        },
      });
    });
  }

  getMobileFeedProctoringStatus(mobileFeeStatus?: boolean) {
    return new Promise<MobileFeedDto>((resolve, reject) => {
      this._examinationService
        .getMobileFeedProctoringStatus(mobileFeeStatus)
        .subscribe({
          next: (response) => {
            resolve(response?.data);
          },
          error: (error) => {
            reject(error.error.data);
          },
        });
    });
  }

  isNetworkConnectionEnabled(): boolean {
    return navigator.onLine;
  }
  checkExtensionPresence(): boolean {
    const extensionIndicator = document.getElementById(
      'extension-installed-indicator',
    );
    let existed: boolean = false;
    // console.log(extensionIndicator, 'extensionIndicator')
    if (extensionIndicator !== null) {
      // console.log('Extension is installed.');
      existed = true;
    } else {
      // console.log('Extension is not installed.');
      existed = false;
    }
    return existed;
  }

  async getNoiseCancelledAudioStream(): Promise<MediaStream> {
    // 1️⃣ Get raw microphone stream with best constraints
    const micStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
        sampleRate: 48000,
      },
    });

    this.audioContext = new AudioContext({ sampleRate: 48000 });

    const source = this.audioContext.createMediaStreamSource(micStream);

    // 3️⃣ High‑pass filter (removes low-frequency rumble, fan noise)
    const highPassFilter = this.audioContext.createBiquadFilter();
    highPassFilter.type = 'highpass';
    highPassFilter.frequency.value = 120;

    // 4️⃣ Compressor (smooth voice, reduces sudden noise)
    const compressor = this.audioContext.createDynamicsCompressor();
    compressor.threshold.value = -35;
    compressor.knee.value = 30;
    compressor.ratio.value = 8;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.25;

    // 5️⃣ Noise Gate (key for background noise removal)
    const gainNode = this.audioContext.createGain();
    gainNode.gain.value = 0;

    const analyser = this.audioContext.createAnalyser();
    analyser.fftSize = 2048;

    // Audio graph
    source
      .connect(highPassFilter)
      .connect(compressor)
      .connect(analyser)
      .connect(gainNode);

    // 6️⃣ Create output stream
    const destination = this.audioContext.createMediaStreamDestination();
    gainNode.connect(destination);

    // 7️⃣ Activate noise gate logic
    this.startNoiseGate(analyser, gainNode);

    return destination.stream;
  }

  /**
   * ✅ Noise gate logic (blocks background noise when user is silent)
   */
  private startNoiseGate(analyser: AnalyserNode, gainNode: GainNode): void {
    const dataArray = new Uint8Array(analyser.fftSize);

    const NOISE_THRESHOLD = 18; // ✅ Adjust if needed (15–25 works well)
    const VOICE_GAIN = 1;
    const SILENCE_GAIN = 0;

    const detectNoise = () => {
      analyser.getByteTimeDomainData(dataArray);

      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const value = (dataArray[i] - 128) / 128;
        sum += value * value;
      }

      const rms = Math.sqrt(sum / dataArray.length) * 100;

      // ✅ Open gate only if voice detected
      gainNode.gain.value = rms > NOISE_THRESHOLD ? VOICE_GAIN : SILENCE_GAIN;

      requestAnimationFrame(detectNoise);
    };

    detectNoise();
  }

  stopAudio(): void {
    this.audioContext?.close();
    this.audioContext = undefined;
  }
}
